import React, {
	useEffect,
	useState,
	useCallback,
	useRef,
} from "react";
import {
	Box,
	Grid,
	Paper,
	Typography,
	List,
	ListItem,
	ListItemText,
	Button,
	Snackbar,
	TextField,
	FormControl,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Link
} from "@mui/material";
import Portal from "@mui/base/Portal";
import CircularProgress from "@mui/material/CircularProgress";
import { DataContext } from "../../../App";
import fileDownload from "js-file-download";
import { AlertBox } from "../../Shared/Alert/Alert";
import DropDown from "../../Shared/DropDown";
import {
	Form,
	Formik
} from "formik";
import "./GenerateAReturnLabel.scss";

const LABEL_NOT_READY_MESSAGE = "Return Label is not yet ready, please click 'Download Label' to retry.";
const HISTORY_NOT_READY_MESSAGE = "Return Label is not yet ready, please click 'Download' to retry.";
const LABEL_FAILED_MESSAGE = "The label failed to generate";

const newIdempotencyKey = () => {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}
	return "return-label-" + Date.now() + "-" + Math.random().toString(16).slice(2);
};

const selectionMaterial = (quantities, optionID, reasonID) => JSON.stringify({
	items: Object.fromEntries([...quantities.entries()].sort(([left], [right]) => left.localeCompare(right))),
	option: optionID,
	reason: String(reasonID),
});

const downloadFailureMessage = (error) => (
	error?.response?.status === 422 ? LABEL_FAILED_MESSAGE : ""
);
import useFetchData from "../../Shared/FetchData";
import { LoadingStatusTable } from "../../Shared/LoadingStatus/LoadingStatus";
import { getDisplayDate } from "../../../utils/date";
import {
	capitalizeFirstLetter,
	capitalizeFirstLetterOfEachWord
} from "../../../utils/text";

export default function GenerateAReturnLabel({ orderID, seller, warehouseID, orderNumber, warehouseCurrency, productsList, isVisible, requireCourier }) {
	if (!isVisible) return;

	const context = React.useContext(DataContext);
	const [state, setState] = useState({
		isVisibleGenerateReturnLabelDiv: isVisible,
		isVisibleDownloadReturnLabelButton: false,
		isGenerateReturnLabelButtonDisabled: true,
		downloadLabelIsLoading: false,
		returnLabelIsLoading: false,
		returnLabelStatus: null,
		returnLabelResults: "",
		showConfirmationAlert: false,
		returnLabelParcelID: "",
		returnLabelReturnID: "",
		downloadReturnLabelStatus: false,
		showDownloadConfirmationAlert: false,
		createReturnErrorMessage: "",
		downloadReturnLabelInfoMessage: "",
	});

	const [selectedOptionId, setSelectedOptionId] = useState("");
	const [shippingOptions, setShippingOptions] = useState({ data: [], isLoading: false, errorMessage: "" });

	const [returnReasons, setReturnReasons] = useState({ data: [], isLoading: false });
	const [returnReasonID, setReturnReasonID] = useState("");

	const [itemQuantities, setItemQuantities] = useState(new Map());
	const [refreshedReturnLabels, setRefreshedReturnLabels] = useState(null);
	const idempotency = useRef({ material: "", key: "" });

	const fetchedReturnLabels = useFetchData(useCallback(() => context.dataProvider.getCreatedReturnLabels(seller, orderID), [context.dataProvider, seller, orderID]));
	const returnLabels = Array.isArray(refreshedReturnLabels)
		? refreshedReturnLabels
		: (Array.isArray(fetchedReturnLabels.results) ? fetchedReturnLabels.results : []);

	const selectionIsIncomplete = (quantities, optionID, reasonID) => (
		quantities.size === 0 || !optionID || reasonID === "" || (requireCourier && !optionID)
	);

	const idempotencyKeyForSelection = () => {
		const material = selectionMaterial(itemQuantities, selectedOptionId, returnReasonID);
		if (idempotency.current.material !== material || !idempotency.current.key) {
			idempotency.current = { material, key: newIdempotencyKey() };
		}
		return idempotency.current.key;
	};

	useEffect(() => {
		setRefreshedReturnLabels(null);
	}, [orderID, seller]);

	useEffect(() => {
		if (!orderID || !seller || itemQuantities.size === 0) {
			setShippingOptions({ isLoading: false, data: [], errorMessage: "" });
			setSelectedOptionId("");
			return;
		}

		const items = Object.fromEntries(itemQuantities);
		let cancelled = false;
		setSelectedOptionId("");
		setShippingOptions({ isLoading: true, data: [], errorMessage: "" });
		const timer = setTimeout(() => {
			context.dataProvider.getReturnShippingOptions(seller, orderID, items)
				.then((response) => {
					if (cancelled) return;
					const data = Array.isArray(response?.data) ? response.data : [];
					setShippingOptions({ isLoading: false, data, errorMessage: "" });
				})
				.catch((error) => {
					if (cancelled) return;
					const status = error.response?.status;
					const message = error.response?.data?.message;
					setShippingOptions({
						isLoading: false,
						data: [],
						errorMessage: (status >= 400 && status < 500 && message) ? capitalizeFirstLetter(message) : "",
					});
				});
		}, 300);

		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [itemQuantities, orderID, seller, context.dataProvider]);

	useEffect(() => {
		if (warehouseID) {
			const getReturnReasons = async () => {
				setReturnReasons({ isLoading: true });
				await context.dataProvider.getReturnReasonsForWarehouse(warehouseID)
					.then((response) => {
						setReturnReasons({ isLoading: false, data: response.data.sort((a, b) => a.sendcloud_id > b.sendcloud_id ? -1 : 1) });
					})
					.catch(() => {
						setReturnReasons({ isLoading: false, data: [] });
					});
			};
			getReturnReasons();
		}
	}, [warehouseID, context.dataProvider]);

	const handleOptionChange = (optionID) => {
		setSelectedOptionId(String(optionID));

		setState((previousState) => ({
			...previousState,
			isGenerateReturnLabelButtonDisabled: selectionIsIncomplete(itemQuantities, optionID, returnReasonID),
		}));
	};

	const buildCreateReturnLabelBody = () => ({
		order_id: Number(orderID),
		option_id: selectedOptionId,
		return_reason_id: Number(returnReasonID),
		items: Object.fromEntries(itemQuantities),
		idempotency_key: idempotencyKeyForSelection(),
	});

	const handleReturnReasonChange = (reasonID) => {
		setReturnReasonID(reasonID);
		setState((previousState) => ({
			...previousState,
			isGenerateReturnLabelButtonDisabled: selectionIsIncomplete(itemQuantities, selectedOptionId, reasonID),
		}));
	};

	const CheckboxList = () => {
		const onQuantityChange = (e, sku) => {
			const currentItemQuantities = new Map(itemQuantities);

			if (e.target.value <= 0) {
				currentItemQuantities.delete(sku);
			} else {
				currentItemQuantities.set(sku, parseInt(e.target.value, 10));
			}

			setSelectedOptionId("");
			setItemQuantities(currentItemQuantities);
			setState((previousState) => ({
				...previousState,
				isGenerateReturnLabelButtonDisabled: selectionIsIncomplete(currentItemQuantities, "", returnReasonID),
			}));
		};

		return (
			<>
				<List sx={{ width: "100%", bgcolor: "background.paper", padding: 1 }}>
					{productsList.map((item, index) => {
						const labelId = `checkbox-list-label-${index}`;
						return (
							<ListItem
								key={index}
								sx={{ padding: 0 }}
							>
								<ListItemText id={labelId} primary={item.name} secondary={item.sku} sx={{ maxWidth: "300px" }} />
								<TextField
									label="Quantity to return"
									type="number"
									size="small"
									sx={{ mr: 1, ml: 1, minWidth: 180 }}
									disabled={state.isVisibleDownloadReturnLabelButton}
									defaultValue={itemQuantities.get(item.sku)}
									onChange={(e) => onQuantityChange(e, item.sku)}
									InputProps={{
										inputProps: {
											min: 0, max: item.quantity
										}
									}}
								/> of {item.quantity}
							</ListItem>
						);
					})}
				</List>
			</>
		);
	};

	const GenerateReturnLabelProductItems = () => {
		if (typeof productsList === "object" && productsList !== null) {
			return (
				<>
					{CheckboxList()}
				</>
			);
		} else {
			return (
				<Typography>No items found</Typography>
			);
		}
	};

	const onClickGenerateReturnLabel = async () => {
		if (!seller) return;

		setState(previousState => {
			return {
				...previousState,
				returnLabelIsLoading: true,
				createReturnErrorMessage: "",
				downloadReturnLabelInfoMessage: "",
			};
		});

		const created = await context.dataProvider.createSellerReturnLabelV2(seller, buildCreateReturnLabelBody())
			.then(response => {
				if (!response.data.parcel_id || response.data.parcel_id === "undefined" || !response.data.return_id) {
					setState(previousState => {
						return {
							...previousState,
							returnLabelIsLoading: false,
							showConfirmationAlert: true,
							returnLabelStatus: false,
						};
					});
					return null;
				}
				return response.data;
			})
			.catch(error => {
				const status = error.response?.status;
				const message = error.response?.data?.message;
				setState(previousState => {
					return {
						...previousState,
						returnLabelIsLoading: false,
						showConfirmationAlert: true,
						returnLabelStatus: false,
						createReturnErrorMessage: (status >= 400 && status < 500 && message) ? capitalizeFirstLetter(message) : "",
					};
				});
				return null;
			});

		if (!created) {
			return;
		}

		setState(previousState => {
			return {
				...previousState,
				returnLabelIsLoading: false,
				returnLabelStatus: true,
				showConfirmationAlert: true,
				returnLabelParcelID: created.parcel_id,
				returnLabelReturnID: created.return_id,
				isVisibleDownloadReturnLabelButton: true,
			};
		});

		setItemQuantities(new Map());
		await context.dataProvider.getCreatedReturnLabels(seller, orderID)
			.then((response) => {
				if (response?.status === 200 && Array.isArray(response.data)) {
					setRefreshedReturnLabels(response.data);
				}
			})
			.catch(() => null);
	};

	const onClickDownloadReturnLabel = async () => {
		if (!seller || !state.returnLabelParcelID || !state.returnLabelReturnID) return;

		setState(previousState => {
			return {
				...previousState,
				downloadLabelIsLoading: true,
				downloadReturnLabelInfoMessage: "",
			};
		});

		await context.dataProvider.downloadSellerReturnLabelV2(seller, orderID, state.returnLabelParcelID, state.returnLabelReturnID)
			.then(response => {
				if (response.status === 202) {
					return setState(previousState => {
						return {
							...previousState,
							downloadLabelIsLoading: false,
							showConfirmationAlert: true,
							returnLabelStatus: false,
							downloadReturnLabelInfoMessage: LABEL_NOT_READY_MESSAGE,
						};
					});
				}

				if (response.status !== 200) {
					return setState(previousState => {
						return {
							...previousState,
							downloadLabelIsLoading: false,
							showConfirmationAlert: true,
							isVisibleDownloadReturnLabelButton: true,
							returnLabelStatus: false,
							downloadReturnLabelInfoMessage: "",
						};
					});
				}

				fileDownload(response.data, orderNumber + "_Return_Label.pdf");

				setState(previousState => {
					return {
						...previousState,
						downloadLabelIsLoading: false,
						showConfirmationAlert: true,
						returnLabelStatus: true,
						downloadReturnLabelInfoMessage: "",
					};
				});
			})
			.catch((error) => {
				setState(previousState => {
					return {
						...previousState,
						downloadLabelIsLoading: false,
						showConfirmationAlert: true,
						returnLabelStatus: false,
						downloadReturnLabelInfoMessage: downloadFailureMessage(error),
					};
				});
			});

	};

	const OptionWarnings = () => {
		const selected = shippingOptions.data?.find((option) => option.option_id === selectedOptionId);
		const warnings = selected?.warnings || [];
		if (warnings.length === 0) return null;

		return (
			<Box className="courier-description">
				{warnings.map((warning, index) => (
					<Grid item xs={12} key={index} sx={{ mb: 1, pl: 1, pr: 1 }} className="warning-message">
						<AlertBox severity="warning" message={warning} />
					</Grid>
				))}
			</Box>
		);
	};

	const GenerateAReturnLabelConfirmation = () => {
		const handleClose = (event, reason) => {
			if (reason === "clickaway") {
				return;
			}
			setState(previousState => {
				return {
					...previousState,
					showConfirmationAlert: false,
					downloadReturnLabelInfoMessage: "",
				};
			});
		};

		return (
			(state.returnLabelStatus !== null) && <Portal>
				<Snackbar open={state.showConfirmationAlert} autoHideDuration={12000} onClose={handleClose}>
					<Box>
						{(state.downloadReturnLabelInfoMessage !== "" ?
							<AlertBox
								severity={state.downloadReturnLabelInfoMessage === LABEL_NOT_READY_MESSAGE ? "info" : "error"}
								message={state.downloadReturnLabelInfoMessage}
							/> :
							(!state.isVisibleDownloadReturnLabelButton ?
								(state.returnLabelStatus === true ?
									<AlertBox
										severity="success"
										message="Return Label created successfully"
									/>
									:
									(state.createReturnErrorMessage !== "" ?
										<AlertBox
											severity="error"
											message={"Error creating Return Label: " + state.createReturnErrorMessage}
										/>
										:
										<AlertBox
											severity="error"
											message="Error creating Return Label, please try again. If this problem
											continues then contact support@gonini.com"
										/>))
								:
								(state.returnLabelStatus === true ?
									<AlertBox
										severity="success"
										message="Return Label downloaded successfully"
									/>
									:
									<AlertBox
										severity="error"
										message="Error downloading Return Label, please wait a moment and try again.
										If this problem continues then contact support@gonini.com"
									/>))
						)}
					</Box>
				</Snackbar>
			</Portal>
		);
	};

	const onClickDownloadExistingLabel = async (label) => {
		setState(previousState => {
			return {
				...previousState,
				downloadReturnLabelInfoMessage: "",
			};
		});

		const download = label.in_label_request
			? context.dataProvider.downloadSellerReturnLabelV2(seller, orderID, label.parcel_id, label.return_id)
			: context.dataProvider.downloadSellerReturnLabel(seller, orderNumber, label.parcel_id);

		await download
			.then(response => {
				if (response.status === 202) {
					return setState(previousState => {
						return {
							...previousState,
							showDownloadConfirmationAlert: true,
							downloadReturnLabelStatus: false,
							downloadReturnLabelInfoMessage: HISTORY_NOT_READY_MESSAGE,
						};
					});
				}

				const tooSmall = !label.in_label_request && response.data.size < 1000;
				if (response.status !== 200 || tooSmall) {
					return setState(previousState => {
						return {
							...previousState,
							showDownloadConfirmationAlert: true,
							downloadReturnLabelStatus: false,
							downloadReturnLabelInfoMessage: "",
						};
					});
				}

				fileDownload(response.data, orderNumber + "_Return_Label.pdf");

				setState(previousState => {
					return {
						...previousState,
						showDownloadConfirmationAlert: true,
						downloadReturnLabelStatus: true,
						downloadReturnLabelInfoMessage: "",
					};
				});
			})
			.catch((error) => {
				setState(previousState => {
					return {
						...previousState,
						showDownloadConfirmationAlert: true,
						downloadReturnLabelStatus: false,
						downloadReturnLabelInfoMessage: downloadFailureMessage(error),
					};
				});
			});
	};

	const ReturnLabelsTableData = () => {
		if (fetchedReturnLabels.isLoading && refreshedReturnLabels === null) {
			return (
				<LoadingStatusTable message="Loading ..." colSpan={10} />
			);
		}

		if (!fetchedReturnLabels.status && refreshedReturnLabels === null) return;

		if (returnLabels.length > 0) {
			return (
				([...returnLabels].sort((b, a) => a.created_at < b.created_at ? -1 : 1).map((label, index) =>
					<TableRow key={index}>
						<TableCell className="tc-wrap">{getDisplayDate(label.created_at)}</TableCell>
						<TableCell className="tc-wrap">{Number(label.seller_price || 0).toFixed(2) + " " + warehouseCurrency}</TableCell>
						<TableCell className="tc-wrap">{capitalizeFirstLetterOfEachWord(label.status || "")}</TableCell>
						<TableCell className="tc-wrap">
							{label.tracking_url ?
								<Link
									href={label.tracking_url}
									target="_blank"
									style={{ textDecoration: "none" }}
								>
									{label.tracking_number}
								</Link>
								:
								label.tracking_number
							}
						</TableCell>
						<TableCell className="tc-wrap">
							<Button
								variant="contained"
								sx={{ mr: 1, mb: 1 }}
								disabled={!label.parcel_id || (label.in_label_request && !label.return_id)}
								color="info"
								onClick={() => onClickDownloadExistingLabel(label)}
							>
								Download
							</Button>
						</TableCell>
					</TableRow>
				))
			);
		}
	};

	const ReturnLabelsTable = () => {
		return (
			<>
				<Typography component="h2" variant="h6" mb={2} mt={2}>
					Return labels for order
				</Typography>
				<TableContainer component={Paper} style={{ maxHeight: 300 }}>
					<Table sx={{ minWidth: 650 }} aria-label="simple table" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell align="left">Created Date</TableCell>
								<TableCell align="left">Cost</TableCell>
								<TableCell align="left">Status</TableCell>
								<TableCell align="left">Tracking Number</TableCell>
								<TableCell align="left"></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{
								fetchedReturnLabels.isLoading && refreshedReturnLabels === null ?
									<LoadingStatusTable colSpan={7} message={"Loading..."} />
									:
									ReturnLabelsTableData()
							}
						</TableBody>
					</Table>
				</TableContainer>
				{DownloadReturnLabelConfirmation()}
			</>
		);
	};

	const DownloadReturnLabelConfirmation = () => {
		const handleClose = (event, reason) => {
			if (reason === "clickaway") {
				return;
			}
			setState(previousState => {
				return {
					...previousState,
					showDownloadConfirmationAlert: false,
					downloadReturnLabelInfoMessage: "",
				};
			});
		};

		return (
			(state.downloadReturnLabelStatus !== null) && <Portal>
				<Snackbar open={state.showDownloadConfirmationAlert} autoHideDuration={12000} onClose={handleClose}>
					<Box>
						{							(state.downloadReturnLabelStatus === true ?
							<AlertBox
								severity="success"
								message="Return Label downloaded successfully"
							/>
							:
							(state.downloadReturnLabelInfoMessage !== "" ?
								<AlertBox
									severity={state.downloadReturnLabelInfoMessage === HISTORY_NOT_READY_MESSAGE ? "info" : "error"}
									message={state.downloadReturnLabelInfoMessage}
								/>
								:
								<AlertBox
									severity="error"
									message="The label download failed, please wait a moment and try again. If this problem continues then contact support@gonini.com"
								/>))}
					</Box>
				</Snackbar>
			</Portal>
		);
	};

	const GenerateAReturnLabelHiddenDiv = () => {
		return (
			<Box mt={0} mb={4}>
				<Formik
					enableReinitialize
					validateOnChange={true}
					validateOnBlur={true}
					initialValues={{}}
					onSubmit={() => {
					}}>
					<Form noValidate autoComplete="off">
						<Paper sx={{ p: 2, width: "100%" }} elevation={2}>
							<Box mb={0}>
								<Grid container spacing={1}>
									<Grid item xs={12}>
										<Grid item xs="auto">
											<Typography component="h2" variant="h6">
												Generate a Return Label
											</Typography>
										</Grid>
										<Grid item xs />
									</Grid>
									<Grid item xs={12} mt={1}>
										<Typography component="h6" sx={{ fontWeight: 900 }}>
											Returned Items:
										</Typography>
									</Grid>
									<Grid item xs={12}>
										{GenerateReturnLabelProductItems()}
									</Grid>
									<Grid item xs={12}>
										{
											itemQuantities.size === 0 ?
												<Typography sx={{ m: 1 }}>
													Select item quantities to see return shipping options.
												</Typography>
												:
												shippingOptions.isLoading ?
													(
														<Typography sx={{ m: 1 }}>
															<CircularProgress size={15} sx={{ mr: 0.5 }} />Loading...
														</Typography>
													)
													:
													shippingOptions.data.length > 0 ?
														(
															<FormControl sx={{ minWidth: 250 }}>
																<DropDown
																	id="return_option"
																	name="return_option"
																	label="Return option"
																	onChange={handleOptionChange}
																	value={selectedOptionId || ""}
																	data={shippingOptions.data.map((option) => ({
																		key: option.option_id,
																		value: option.option_id,
																		name: option.display_name + " - " + Number(option.seller_price || 0).toFixed(2) + " " + (option.currency || warehouseCurrency || ""),
																	}))}
																	required={true}
																/>
															</FormControl>
														)
														:
														<AlertBox
															severity="error"
															message={shippingOptions.errorMessage || "No available return options found. Please contact support@gonini.com for a return label."}
														/>
										}
									</Grid>
									<Grid item xs={12}>
										{
											returnReasons.isLoading ?
												(
													<Typography sx={{ m: 1 }}>
														<CircularProgress size={15} sx={{ mr: 0.5 }} />Loading...
													</Typography>
												)
												:
												returnReasons.data.length > 0 && returnReasons.data !== null ?
													(
														<FormControl sx={{ minWidth: 250 }}>
															<DropDown
																id="return_reason"
																name="return_reason"
																label="Return Reason"
																onChange={handleReturnReasonChange}
																value={returnReasonID}
																data={returnReasons.data.map((r) => ({
																	key: r.id,
																	value: r.sendcloud_id,
																	name: r.name,
																}))}
																required={true}
															/>
														</FormControl>
													)
													:
													<AlertBox
														severity="error"
														message="Could not fetch available Return Reasons. Please contact support@gonini.com for a return label."
													/>
										}
									</Grid>
									{OptionWarnings()}
									<Grid item xs={12} container spacing={2} mb={2}>
										<Grid item xs="auto" sx={{ mt: 2 }}>
											<Button
												variant="contained"
												disabled={state.isGenerateReturnLabelButtonDisabled || state.isVisibleDownloadReturnLabelButton || state.returnLabelIsLoading}
												onClick={onClickGenerateReturnLabel}
											>
												Generate Label {state.returnLabelIsLoading &&
													<CircularProgress
														sx={{ color: "#FFFFFF", marginLeft: "0.5rem" }} size={15} />}
											</Button>
										</Grid>
										{state.isVisibleDownloadReturnLabelButton &&
											<Grid item xs="auto" sx={{ mt: 2 }}>
												<Button
													variant="contained"
													onClick={onClickDownloadReturnLabel}
												>
													Download Label {state.downloadLabelIsLoading &&
														<CircularProgress
															sx={{ color: "#FFFFFF", marginLeft: "0.5rem" }}
															size={15} />}
												</Button>
											</Grid>
										}
									</Grid>
									<Grid item xs={12}>
										<AlertBox
											severity="info"
											message="If a label has been created, but is currently unavailable for download, please wait a moment and click 'Download Label' again.
											If after 5 minutes this is still unavailable please contact support@gonini.com."
										/>
									</Grid>
									{
										returnLabels.length > 0 &&
										<Grid item xs={12} mt={1}>
											{ReturnLabelsTable()}
										</Grid>
									}
								</Grid>
							</Box>
						</Paper>
					</Form>
				</Formik>
			</Box>
		);
	};

	return (
		<>
			{state.isVisibleGenerateReturnLabelDiv && GenerateAReturnLabelHiddenDiv()}
			{GenerateAReturnLabelConfirmation()}
		</>
	);

}
